import { useState } from 'react';
import { useRef } from 'react';
import { Plus, Trash2, Monitor, Download, Copy, Upload, Scissors } from 'lucide-react';
import { toast } from 'sonner';
import type { CanvasGroup, CanvasGroupMonitorRef, CanvasGroupGeometry } from '@/lib/animation/types';
import { buildSpec, renderGuideImage, downloadJson, downloadGuideImage, copyPromptToClipboard } from '@/lib/animation/exportSpec';
import { sliceVideo, downloadSlice, type SliceResult } from '@/lib/animation/sliceVideo';

interface WallDef {
  id: string; name: string;
  monitors: { id: string; name: string; w: number; h: number; bt: number; br: number; bb: number; bl: number; nw: number; nh: number }[];
}

interface Props {
  groups: CanvasGroup[];
  walls: WallDef[];
  selectedGroupId: string | null;
  onSelectGroup: (id: string | null) => void;
  onAddGroup: () => void;
  onDeleteGroup: (id: string) => void;
  onUpdateGroup: (id: string, patch: Partial<CanvasGroup>) => void;
  getGeometry: (group: CanvasGroup) => CanvasGroupGeometry | null;
}

export function CanvasGroupPanel({
  groups, walls, selectedGroupId, onSelectGroup,
  onAddGroup, onDeleteGroup, onUpdateGroup, getGeometry,
}: Props) {
  const [addingMonitorTo, setAddingMonitorTo] = useState<string | null>(null);
  const [slicing, setSlicing] = useState(false);
  const [sliceProgress, setSliceProgress] = useState(0);
  const [sliceResults, setSliceResults] = useState<SliceResult[] | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);

  // All monitors across all walls, for the "add monitor" picker
  const allMonitors = walls.flatMap((w) => w.monitors.map((m) => ({ wallId: w.id, wallName: w.name, ...m })));

  const addMonitorToGroup = (groupId: string, wallId: string, monitorId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    // Don't add duplicates
    if (group.monitors.some((m) => m.wallId === wallId && m.monitorId === monitorId)) return;
    const wall = walls.find((w) => w.id === wallId);
    const mon = wall?.monitors.find((m) => m.id === monitorId);
    const stationId = (mon as any)?.stationId ?? null;
    onUpdateGroup(groupId, {
      monitors: [...group.monitors, { wallId, monitorId, stationId }],
    });
    setAddingMonitorTo(null);
  };

  const removeMonitorFromGroup = (groupId: string, index: number) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    onUpdateGroup(groupId, {
      monitors: group.monitors.filter((_, i) => i !== index),
    });
  };

  const moveMonitor = (groupId: string, from: number, to: number) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group || to < 0 || to >= group.monitors.length) return;
    const mons = [...group.monitors];
    const [item] = mons.splice(from, 1);
    mons.splice(to, 0, item);
    onUpdateGroup(groupId, { monitors: mons });
  };

  const resolveMonitorName = (ref: CanvasGroupMonitorRef) => {
    const wall = walls.find((w) => w.id === ref.wallId);
    const mon = wall?.monitors.find((m) => m.id === ref.monitorId);
    return { wallName: wall?.name ?? '?', monName: mon?.name ?? '?' };
  };

  // Detail view for selected group
  if (selectedGroup) {
    const geo = getGeometry(selectedGroup);
    const groupMonitorIds = new Set(selectedGroup.monitors.map((m) => `${m.wallId}:${m.monitorId}`));
    const available = allMonitors.filter((m) => !groupMonitorIds.has(`${m.wallId}:${m.id}`));

    return (
      <div className="flex flex-col h-full">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <div>
            <button onClick={() => onSelectGroup(null)}
              className="text-[10px] font-mono text-cyan-500 hover:text-cyan-400 uppercase tracking-wider">
              &larr; All Groups
            </button>
            <div className="text-sm font-medium mt-0.5">{selectedGroup.name}</div>
          </div>
          <button onClick={() => { onDeleteGroup(selectedGroup.id); onSelectGroup(null); }}
            className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded transition">
            <Trash2 size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 block mb-1">Name</label>
            <input type="text" value={selectedGroup.name}
              onChange={(e) => onUpdateGroup(selectedGroup.id, { name: e.target.value })}
              className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500" />
          </div>

          {/* Geometry stats */}
          {geo && (
            <div className="bg-slate-950 border border-slate-800 rounded p-3 space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Virtual canvas</div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400 font-mono">Render</span>
                <span className="text-cyan-400 font-mono">{geo.renderWidth.toLocaleString()} x {geo.renderHeight.toLocaleString()} px</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400 font-mono">Physical</span>
                <span className="text-slate-300 font-mono">{(geo.totalWidthMm / 1000).toFixed(2)}m x {(geo.maxHeightMm / 1000).toFixed(2)}m</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400 font-mono">Density</span>
                <span className="text-slate-300 font-mono">{geo.density.toFixed(2)} px/mm</span>
              </div>
              {geo.deadZones.length > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400 font-mono">Gaps</span>
                  <span className="text-orange-400 font-mono">{geo.deadZones.length} dead zone{geo.deadZones.length > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )}

          {/* Export spec for video generation */}
          {geo && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">
                Video generation spec
              </div>
              <div className="space-y-1.5">
                <button onClick={() => {
                  const spec = buildSpec(selectedGroup.name, geo);
                  downloadJson(spec);
                  toast.success('JSON spec downloaded');
                }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded transition">
                  <Download size={12} /> Download JSON Spec
                </button>
                <button onClick={() => {
                  const guide = renderGuideImage(geo);
                  downloadGuideImage(guide, selectedGroup.name);
                  toast.success('Guide image downloaded');
                }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded transition">
                  <Download size={12} /> Download Guide Image
                </button>
                <button onClick={() => {
                  const spec = buildSpec(selectedGroup.name, geo);
                  copyPromptToClipboard(spec).then(() => toast.success('Prompt copied to clipboard'));
                }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded transition">
                  <Copy size={12} /> Copy Prompt to Clipboard
                </button>
              </div>
            </div>
          )}

          {/* Video import + auto-slice */}
          {geo && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">
                Import &amp; slice video
              </div>
              <input ref={videoInputRef} type="file" accept="video/*" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !geo) return;
                  setSlicing(true);
                  setSliceProgress(0);
                  setSliceResults(null);
                  try {
                    const results = await sliceVideo(file, geo, setSliceProgress);
                    setSliceResults(results);
                    toast.success(`Sliced into ${results.length} clips`);
                  } catch (err: any) {
                    toast.error(`Slice failed: ${err.message}`);
                  } finally {
                    setSlicing(false);
                    if (videoInputRef.current) videoInputRef.current.value = '';
                  }
                }} />
              <button onClick={() => videoInputRef.current?.click()}
                disabled={slicing}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded transition disabled:opacity-50">
                {slicing ? (
                  <><Scissors size={12} className="animate-pulse" /> Slicing... {Math.round(sliceProgress)}%</>
                ) : (
                  <><Upload size={12} /> Import Video &amp; Auto-Slice</>
                )}
              </button>

              {slicing && (
                <div className="mt-1.5 h-1.5 bg-slate-800 rounded overflow-hidden">
                  <div className="h-full bg-cyan-500 transition-all duration-300" style={{ width: `${sliceProgress}%` }} />
                </div>
              )}

              {sliceResults && (
                <div className="mt-2 space-y-1">
                  {sliceResults.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-slate-950 border border-slate-800 rounded text-xs">
                      <span className="text-slate-200 flex-1 truncate">{r.monitorName}</span>
                      <span className="text-slate-500 font-mono text-[10px]">{(r.blob.size / 1024 / 1024).toFixed(1)}MB</span>
                      <button onClick={() => downloadSlice(r)}
                        className="p-1 text-cyan-500 hover:text-cyan-400">
                        <Download size={12} />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => sliceResults.forEach((r) => downloadSlice(r))}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs bg-cyan-900 hover:bg-cyan-800 border border-cyan-700 rounded font-medium transition">
                    <Download size={12} /> Download All
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Monitor list (ordered) */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">
              Monitors ({selectedGroup.monitors.length})
            </div>
            <div className="space-y-1">
              {selectedGroup.monitors.map((ref, i) => {
                const { wallName, monName } = resolveMonitorName(ref);
                return (
                  <div key={`${ref.wallId}-${ref.monitorId}`}
                    className="flex items-center gap-1 px-2 py-1.5 bg-slate-950 border border-slate-800 rounded text-xs">
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button onClick={() => moveMonitor(selectedGroup.id, i, i - 1)}
                        disabled={i === 0}
                        className="text-slate-600 hover:text-slate-300 disabled:opacity-20 text-[10px] leading-none">&#9650;</button>
                      <button onClick={() => moveMonitor(selectedGroup.id, i, i + 1)}
                        disabled={i === selectedGroup.monitors.length - 1}
                        className="text-slate-600 hover:text-slate-300 disabled:opacity-20 text-[10px] leading-none">&#9660;</button>
                    </div>
                    <span className="text-slate-500 font-mono text-[10px] w-5 text-center shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-200 truncate">{monName}</div>
                      <div className="text-[10px] text-slate-600 font-mono truncate">{wallName}</div>
                    </div>
                    <button onClick={() => removeMonitorFromGroup(selectedGroup.id, i)}
                      className="p-1 text-slate-600 hover:text-red-400 shrink-0">
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Add monitor */}
            {addingMonitorTo === selectedGroup.id ? (
              <div className="mt-2 bg-slate-950 border border-slate-700 rounded shadow-xl max-h-48 overflow-y-auto">
                {available.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500 italic">All monitors added</div>
                ) : (
                  walls.map((wall) => {
                    const wallAvail = available.filter((m) => m.wallId === wall.id);
                    if (wallAvail.length === 0) return null;
                    return (
                      <div key={wall.id}>
                        <div className="px-3 py-1 text-[10px] font-mono text-slate-500 uppercase tracking-wider bg-slate-900 sticky top-0">
                          {wall.name}
                        </div>
                        {wallAvail.map((m) => (
                          <button key={m.id}
                            onClick={() => addMonitorToGroup(selectedGroup.id, wall.id, m.id)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-800 flex items-center gap-2">
                            <Monitor size={12} className="text-slate-500" />
                            <span className="text-slate-200">{m.name}</span>
                            <span className="text-[10px] font-mono text-slate-600 ml-auto">{m.nw}x{m.nh}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })
                )}
                <button onClick={() => setAddingMonitorTo(null)}
                  className="w-full px-3 py-1.5 text-xs text-slate-500 hover:text-slate-300 border-t border-slate-800">
                  Cancel
                </button>
              </div>
            ) : (
              <button onClick={() => setAddingMonitorTo(selectedGroup.id)}
                className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded transition">
                <Plus size={12} /> Add Monitor
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-800">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Canvas Groups</div>
        <div className="text-sm font-medium mt-0.5">
          {groups.length} group{groups.length !== 1 ? 's' : ''}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {groups.length === 0 && (
          <p className="text-xs text-slate-500 italic">
            No canvas groups yet. Create one to combine monitors from different walls into a single animation canvas.
          </p>
        )}
        <div className="space-y-1.5">
          {groups.map((g) => {
            const geo = getGeometry(g);
            return (
              <button key={g.id} onClick={() => onSelectGroup(g.id)}
                className="w-full text-left px-3 py-2.5 bg-slate-950 border border-slate-800 hover:border-cyan-700 rounded transition">
                <div className="text-xs text-slate-200 font-medium">{g.name}</div>
                <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                  {g.monitors.length} monitor{g.monitors.length !== 1 ? 's' : ''}
                  {geo ? ` \u00b7 ${geo.renderWidth.toLocaleString()}x${geo.renderHeight.toLocaleString()}px` : ''}
                </div>
              </button>
            );
          })}
        </div>
        <button onClick={onAddGroup}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded font-medium transition">
          <Plus size={14} /> New Group
        </button>
      </div>
    </div>
  );
}
